import React, { useEffect, useMemo, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useForm, Controller } from 'react-hook-form';
import { extractFieldErrors } from '../utils/formErrorUtils';
import { getLeavePolicy, formatLeaveUnit } from '../utils/leavePolicies';

const LeaveFormModal = ({ visible, onHide, onSubmit, leaveTypes, leaveTypeOptions }) => {
  const startRef = useRef(null);
  const endRef = useRef(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setError,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      reason: '',
      start_date: null,
      end_date: null,
      leave_type_id: null,
    },
  });

  useEffect(() => {
    if (!visible) {
      reset();
    }
  }, [visible, reset]);

  const submit = async (data) => {
    try {
      await onSubmit(data);
    } catch (error) {
      if (error?.response?.status === 422) {
        extractFieldErrors(error).forEach(({ field, message }) => {
          setError(field, { type: 'server', message });
        });
      }
    }
  };

  const selectedLeaveTypeId = watch('leave_type_id');

  const selectedLeaveType = useMemo(
    () => leaveTypes?.find((type) => type.id === selectedLeaveTypeId),
    [leaveTypes, selectedLeaveTypeId]
  );

  const selectedPolicy = useMemo(
    () => getLeavePolicy(selectedLeaveType),
    [selectedLeaveType]
  );

  const unitLabel = selectedLeaveType ? formatLeaveUnit(selectedLeaveType.unit) : null;

  const options = useMemo(() => {
    if (leaveTypeOptions?.length) {
      return leaveTypeOptions;
    }
    return leaveTypes?.map((lt) => ({ label: lt.name, value: lt.id })) ?? [];
  }, [leaveTypeOptions, leaveTypes]);

  return (
    <Dialog header="Yeni İzin Talebi" visible={visible} style={{ width: '500px' }} modal onHide={onHide}>
      <form onSubmit={handleSubmit(submit)} className="p-fluid">
        <label>Açıklama</label>
        <Controller
          name="reason"
          control={control}
          rules={{ required: 'Açıklama zorunludur' }}
          render={({ field }) => (
            <InputText {...field} className={`mb-2 ${errors.reason ? 'p-invalid' : ''}`} />
          )}
        />
        {errors.reason && <small className="p-error">{errors.reason.message}</small>}

        <label>Başlangıç Tarihi</label>
        <Controller
          name="start_date"
          control={control}
          rules={{ required: 'Başlangıç tarihi zorunludur' }}
          render={({ field }) => (
            <Calendar
              ref={startRef}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value);
                if (e?.target?.name === 'hour' || e?.target?.name === 'minute') {
                  startRef.current?.hide();
                }
              }}
              showTime
              showSeconds
              dateFormat="dd.mm.yy"
              className={`mb-2 ${errors.start_date ? 'p-invalid' : ''}`}
            />
          )}
        />
        {errors.start_date && <small className="p-error">{errors.start_date.message}</small>}

        <label>Bitiş Tarihi</label>
        <Controller
          name="end_date"
          control={control}
          rules={{
            required: 'Bitiş tarihi zorunludur',
            validate: (value) => {
              const start = getValues('start_date');
              return !start || !value || value >= start || 'Bitiş tarihi başlangıçtan sonra olmalı';
            },
          }}
          render={({ field }) => (
            <Calendar
              ref={endRef}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value);
                if (e?.target?.name === 'hour' || e?.target?.name === 'minute') {
                  endRef.current?.hide();
                }
              }}
              showTime
              showSeconds
              dateFormat="dd.mm.yy"
              className={`mb-2 ${errors.end_date ? 'p-invalid' : ''}`}
            />
          )}
        />
        {errors.end_date && <small className="p-error">{errors.end_date.message}</small>}

        <label>İzin Tipi</label>
        <Controller
          name="leave_type_id"
          control={control}
          rules={{ required: 'İzin tipi seçilmelidir' }}
          render={({ field }) => (
            <Dropdown
              {...field}
              value={field.value}
              options={options}
              onChange={(e) => field.onChange(e.value)}
              placeholder="Seçiniz"
              className={`mb-2 ${errors.leave_type_id ? 'p-invalid' : ''}`}
            />
          )}
        />
        {errors.leave_type_id && <small className="p-error">{errors.leave_type_id.message}</small>}

        {selectedLeaveType && (
          <div className="mt-3 p-3 border-round surface-100 text-sm">
            <div className="font-medium mb-2">{selectedLeaveType.name} Bilgileri</div>
            <ul className="pl-3 mb-2">
              {selectedLeaveType.default_allocation !== null && selectedLeaveType.default_allocation !== undefined && (
                <li>
                  Varsayılan Kota: {selectedLeaveType.default_allocation} {unitLabel}
                </li>
              )}
              {selectedLeaveType.unit && <li>İzin Birimi: {unitLabel}</li>}
              {selectedLeaveType.carryover && <li>Kullanılmayan izinler bir sonraki döneme devreder.</li>}
            </ul>
            {selectedPolicy ? (
              <>
                <div className="font-medium mb-1 text-color-secondary text-xs">Öne çıkan kurallar:</div>
                <ul className="pl-3 text-xs flex flex-column gap-1">
                  {selectedPolicy.summary.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                {selectedPolicy.notes?.length ? (
                  <div className="text-xs text-color-secondary mt-2">
                    {selectedPolicy.notes.map((note, index) => (
                      <div key={index}>* {note}</div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-xs text-color-secondary">
                Bu izin tipine ait detaylar için lütfen yöneticinizle iletişime geçiniz.
              </div>
            )}
          </div>
        )}

        <div className="text-right mt-3">
          <Button label="Kaydet" icon="pi pi-check" type="submit" />
        </div>
      </form>
    </Dialog>
  );
};

export default LeaveFormModal;
